import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Youtube, Phone, Mail, MapPin } from "lucide-react";
import { SITE, waLink } from "@/lib/site";
import { DESTINATIONS } from "@/lib/data";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-[var(--tx-border)] bg-[var(--tx-bg-2)] pt-20 pb-10"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex flex-col leading-none mb-6">
              <span className="font-display text-3xl font-medium tracking-tight">
                TripTrix Voyages
              </span>
              <span className="text-[0.62rem] uppercase tracking-[0.34em] text-[var(--tx-gold)] mt-1">
                {SITE.tagline}
              </span>
              <span className="text-[0.68rem] tracking-[0.12em] text-[var(--tx-text-2)] mt-2" data-testid="footer-parent-company">
                {SITE.parentNote}
              </span>
            </div>
            <p className="text-[var(--tx-text-2)] leading-relaxed max-w-sm mb-6">
              A <span className="text-[var(--tx-gold)]">B2B-only</span> ground Destination
              Management Company. We partner exclusively with travel agents & tour operators
              across North East India, Bhutan, Nepal & the Darjeeling hills.
            </p>
            <div className="flex items-center gap-3" data-testid="footer-socials">
              {[
                { Icon: Instagram, href: SITE.socials.instagram, label: "Instagram" },
                { Icon: Facebook, href: SITE.socials.facebook, label: "Facebook" },
                { Icon: Linkedin, href: SITE.socials.linkedin, label: "LinkedIn" },
                { Icon: Youtube, href: SITE.socials.youtube, label: "YouTube" },
              ]
                .filter(({ href }) => href)
                .map(({ Icon, href, label }, i) => (
                  <a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    data-testid={`footer-social-${label.toLowerCase()}`}
                    className="grid h-10 w-10 place-items-center rounded-full border border-[var(--tx-border)] text-[var(--tx-text-2)] hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-300"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.24em] text-[var(--tx-text-2)] mb-5">
              Destinations
            </h4>
            <ul className="grid grid-cols-2 gap-y-2.5 gap-x-4">
              {DESTINATIONS.map((d) => (
                <li key={d.slug}>
                  <Link
                    to={`/destinations/${d.slug}`}
                    className="text-sm text-[var(--tx-text)]/75 hover:text-[var(--tx-gold)] transition-colors duration-300"
                  >
                    {d.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.24em] text-[var(--tx-text-2)] mb-5">
              Company
            </h4>
            <ul className="space-y-2.5">
              {[
                ["Our Story", "/about"],
                ["Journeys", "/packages"],
                ["Tour Packages", "/tours"],
                ["Partner Reviews", "/partners"],
                ["Airport & Rail Transfers", "/airport-transfers"],
                ["Journal", "/journal"],
                ["Explore All Tours", "/explore"],
                ["Contact", "/contact"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-sm text-[var(--tx-text)]/75 hover:text-[var(--tx-gold)] transition-colors duration-300"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.24em] text-[var(--tx-text-2)] mb-5">
              Reach Us
            </h4>
            <ul className="space-y-4 text-sm text-[var(--tx-text)]/75">
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-[var(--tx-gold)]" />
                {SITE.addressLine}
              </li>
              <li className="pl-7 -mt-2 space-y-2 text-[var(--tx-text-2)]">
                {SITE.offices.map((o) => (
                  <span key={o.city} className="block">
                    <span className="text-[var(--tx-text)]">{o.label} — {o.city}:</span>{" "}
                    {o.lines.join(", ")}
                  </span>
                ))}
              </li>
              <li>
                <a href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-3 hover:text-[var(--tx-gold)] transition-colors duration-300">
                  <Phone className="h-4 w-4 text-[var(--tx-gold)]" /> {SITE.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 hover:text-[var(--tx-gold)] transition-colors duration-300">
                  <Mail className="h-4 w-4 text-[var(--tx-gold)]" /> {SITE.email}
                </a>
              </li>
              <li>
                <a href={waLink()} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border border-[var(--tx-gold)] px-4 py-2 text-[var(--tx-gold)] hover:bg-[var(--tx-gold)] hover:text-[var(--tx-bg)] transition-colors duration-300">
                  Chat on WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-[var(--tx-border)] flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[var(--tx-text-2)]">
          <p>© {year} TripTrix Voyages — {SITE.parentNote}. {SITE.established}. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[var(--tx-gold)] transition-colors duration-300">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-[var(--tx-gold)] transition-colors duration-300">Terms</Link>
            <Link to="/cancellation" data-testid="footer-cancellation" className="hover:text-[var(--tx-gold)] transition-colors duration-300">Cancellation</Link>
          </div>
          <p className="uppercase tracking-[0.16em]" data-testid="footer-gst">
            GST {SITE.gst} · {SITE.msme}
          </p>
        </div>
        <div className="mt-6 text-center text-[0.65rem] uppercase tracking-[0.28em] text-[var(--tx-text-2)]/70" data-testid="footer-credit">
          Concept, Craft &amp; Creation by{" "}
          <a
            href={SITE.founderLinkedin}
            target="_blank"
            rel="noreferrer"
            className="text-[var(--tx-gold)] hover:underline"
          >
            Satyam Singh
          </a>
        </div>
      </div>
    </footer>
  );
}
